import React, { lazy, Suspense, useState } from 'react';
import { CgProfile } from 'react-icons/cg';
import ReactDOM from 'react-dom';
import { IoClose } from 'react-icons/io5';
import { useEffect } from 'react';
import {
  FiFileText,
  FiUsers,
  FiCalendar,
  FiZap,
  FiMail,
  FiCpu,
  FiFolder,
} from 'react-icons/fi';

import { UserAvatar } from './UserAvatar';
import { DataState } from '../context/DataProvider';
import { useAccess } from '../services/access';
import UserManagementContent from './modals/UserManagementContent';
import MeetingsContent from './modals/MeetingsContent';
import SkillsContent from './modals/SkillsContent';
import MailSettingsContent from './modals/MailSettingsContent';
import MemorySettingsContent from './modals/MemorySettingsContent';
import MailIntelSettingsContent from './modals/MailIntelSettingsContent';
import DocumentHistorySettingsContent from './modals/DocumentHistorySettingsContent';
import {
  ENABLE_MAIL_INTEL,
  ENABLE_MAIL_SIGNATURE,
  ENABLE_MEETINGS,
  ENABLE_SHAREPOINT,
  ENABLE_SKILLS,
  ENABLE_REPORT_TEMPLATES,
  ENABLE_USER_MANAGEMENT,
  ENABLE_USER_MEMORY,
} from '../config/features';

const modalRoot = document.getElementById('modal-root');

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-[96vw]',
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  headerActions,
  size = '6xl',
  compact = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Use ReactDOM.createPortal to render the modal outside of the current component hierarchy
  return ReactDOM.createPortal(
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-65 p-3 backdrop-blur-sm transition-opacity md:p-6"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className={`text-p-text relative flex h-[88vh] max-h-[92vh] w-[94vw] flex-col rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl ${sizeClasses[size] || 'max-w-6xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-center justify-between border-b border-slate-700/80 ${
            compact ? 'px-4 py-2' : 'px-6 py-4'
          }`}
        >
          <h3
            className={`font-semibold text-white ${compact ? 'text-sm' : 'text-xl'}`}
          >
            {title}
          </h3>

          <div className="flex items-center gap-4">
            {headerActions}

            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-slate-700 hover:text-white"
            >
              <IoClose size={compact ? 18 : 24} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div
          className={`min-h-0 flex-1 overflow-hidden ${compact ? 'p-2' : 'p-4 md:p-6'}`}
        >
          {children}
        </div>
      </div>
    </div>,
    modalRoot,
  );
};

const ProfileContent = ({ userInfo }) => (
  <div>
    <h2 className="mb-6 text-lg font-semibold text-slate-100">
      Profile Information
    </h2>
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
      <UserAvatar
        user={userInfo}
        className="h-20 w-20 flex-shrink-0 border-4 border-slate-700 text-3xl"
      />
      <div className="mt-1 flex flex-col gap-4 text-center sm:text-left">
        <div>
          <p className="text-xs text-slate-400">Name</p>
          <p className="text-sm text-slate-100">{userInfo.name}</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Email</p>
          <p className="break-all text-sm text-slate-100">{userInfo.email}</p>
        </div>
      </div>
    </div>
  </div>
);

// The left list of Account Settings, grouped by what the entries are about.
// An entry whose feature is switched off, or whose `permission` (one name, or
// a list of which any one is enough) the role lacks, is left out -- it is the
// permission its content loads with, so a shown tab never fails to load. A
// group with no entries left is left out too.
const NAV_GROUPS = [
  {
    title: 'Account',
    items: [
      { id: 'profile', label: 'Profile', Icon: CgProfile, enabled: true },
    ],
  },
  {
    title: 'Project',
    items: [
      { id: 'projects', label: 'Projects', Icon: FiFolder, enabled: true },
      {
        id: 'users',
        label: 'Team Directory',
        Icon: FiUsers,
        enabled: ENABLE_USER_MANAGEMENT,
        // The list itself needs this; without it the tab could only fail.
        permission: 'user_management:view',
      },
      {
        id: 'meetings',
        label: 'Meetings',
        Icon: FiCalendar,
        enabled: ENABLE_MEETINGS,
        permission: 'meeting:view',
      },
      {
        id: 'document-history',
        label: 'Document History',
        Icon: FiFileText,
        enabled: ENABLE_SHAREPOINT,
        permission: 'file:view',
      },
    ],
  },
  {
    title: 'Assistant',
    items: [
      {
        id: 'skills',
        label: 'Skills',
        Icon: FiZap,
        enabled: ENABLE_SKILLS,
        permission: 'template:view',
      },
      {
        id: 'memory',
        label: 'Memory',
        Icon: FiCpu,
        enabled: ENABLE_USER_MEMORY,
        // Memories belong to the assistant, which needs this.
        permission: 'chat:llm:use',
      },
      {
        id: 'report-templates',
        label: 'Report Templates',
        Icon: FiFileText,
        enabled: ENABLE_REPORT_TEMPLATES,
        permission: 'template:view',
      },
    ],
  },
  {
    title: 'Mail',
    items: [
      {
        // One place for mail: signature, and the Outlook connection that
        // mail intelligence reads.
        id: 'mail',
        label: 'Mail',
        Icon: FiMail,
        enabled: ENABLE_MAIL_SIGNATURE || ENABLE_MAIL_INTEL,
        // Either section is enough to show the tab; each section checks its own.
        permission: ['mail_intel:mailbox:connect', 'outlook:view'],
      },
    ],
  },
];

// The project token names the project the user is working in.
const readProjectToken = (token) => {
  try {
    return { projectId: JSON.parse(atob(token.split('.')[1])).project_id };
  } catch (error) {
    return {};
  }
};

// Loaded when their tab is opened.
const ReportTemplates = lazy(
  () => import('../pages/report-templates/ReportTemplates'),
);
const ProjectsDemo = lazy(() => import('../ProjectsDemo'));
const DocumentChangeHistory = lazy(
  () => import('../pages/sharepoint-test/DocumentChangeHistory'),
);

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [reportTemplatesOpen, setReportTemplatesOpen] = useState(false);
  const [documentHistoryOpen, setDocumentHistoryOpen] = useState(false);
  const { userInfo, userGoogleId: accessToken } = DataState();
  const { can } = useAccess();

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab('profile');
    }
  }, [isOpen]);

  const displayUser = userInfo || {
    name: 'Guest User',
    email: 'Not Logged In',
    picture: '',
  };

  const tabClass = (tab) =>
    `flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs font-medium transition-all duration-200 ${
      activeTab === tab
        ? 'bg-[#5652CF] text-white shadow-md'
        : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
    }`;

  // Working in another project changes every list in the app, so the app is
  // loaded again with that project's token.
  const switchProject = (projectToken) => {
    localStorage.setItem('projectToken', projectToken);
    window.location.assign('/chat');
  };

  const openDocumentHistory = () => setDocumentHistoryOpen(true);

  return (
    <>
      <Modal
        title="Account Settings"
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
      >
        {/* The main flex container filling full modal height */}
        <div className="flex h-full min-h-0 flex-col gap-4 md:flex-row md:gap-8">
          {/* Left Pane: Navigation */}
          <div className="h-full w-full flex-shrink-0 overflow-y-auto border-b border-slate-700/60 pb-4 md:w-52 md:border-b-0 md:border-r md:pb-0 md:pr-6">
            <nav className="flex flex-row gap-2 md:flex-col md:gap-1">
              {NAV_GROUPS.map((group) => {
                const items = group.items.filter(
                  (item) =>
                    item.enabled &&
                    (!item.permission || [].concat(item.permission).some(can)),
                );
                if (items.length === 0) return null;
                return (
                  <React.Fragment key={group.title}>
                    {/* A line between groups; the title is only a key. */}
                    <div className="hidden border-t border-slate-700/60 first:hidden md:my-1.5 md:block" />
                    {items.map(({ id, label, Icon }) => (
                      <button
                        key={id}
                        // Report templates open on top of this window; closing
                        // them comes back here.
                        onClick={() =>
                          id === 'report-templates'
                            ? setReportTemplatesOpen(true)
                            : setActiveTab(id)
                        }
                        className={tabClass(id)}
                      >
                        <Icon className="flex-shrink-0 text-lg" />
                        <span>{label}</span>
                      </button>
                    ))}
                  </React.Fragment>
                );
              })}
            </nav>
          </div>

          {/* Right Pane: Content */}
          <div className="custom-scrollbar flex h-full min-h-0 flex-1 flex-col overflow-y-auto pr-1 md:mt-0">
            {activeTab === 'profile' && (
              <ProfileContent userInfo={displayUser} />
            )}
            {ENABLE_SKILLS && activeTab === 'skills' && <SkillsContent />}
            {activeTab === 'mail' && (
              <div className="space-y-8">
                {ENABLE_MAIL_INTEL && can('mail_intel:mailbox:connect') && (
                  <MailIntelSettingsContent />
                )}
                {ENABLE_MAIL_SIGNATURE && can('outlook:view') && (
                  <MailSettingsContent />
                )}
              </div>
            )}
            {ENABLE_USER_MEMORY && activeTab === 'memory' && (
              <MemorySettingsContent />
            )}
            {ENABLE_SHAREPOINT && activeTab === 'document-history' && (
              <DocumentHistorySettingsContent onOpen={openDocumentHistory} />
            )}
            {ENABLE_USER_MANAGEMENT &&
              activeTab === 'users' &&
              can('user_management:view') && <UserManagementContent />}
            {ENABLE_MEETINGS && activeTab === 'meetings' && (
              <MeetingsContent accessToken={accessToken} />
            )}
            {/* Shown here, inside the window, so the user stays where they were. */}
            <Suspense
              fallback={<p className="text-xs text-slate-400">Loading…</p>}
            >
              {activeTab === 'projects' && (
                <ProjectsDemo
                  embedded
                  userIdToken={localStorage.getItem('userToken')}
                  currentProjectId={
                    readProjectToken(accessToken || '').projectId
                  }
                  onProjectAccess={switchProject}
                  handleStorageChange={() => {}}
                />
              )}
            </Suspense>
          </div>
        </div>
      </Modal>

      {/* Report templates have their own window: a list and a wide
            preview need more room than a settings tab. It opens on top of
            Account Settings, so closing it returns the user there. */}
      <Modal
        title="Report Templates"
        isOpen={reportTemplatesOpen}
        onClose={() => setReportTemplatesOpen(false)}
        size="7xl"
        compact
      >
        <Suspense fallback={<p className="text-xs text-slate-400">Loading…</p>}>
          <ReportTemplates embedded />
        </Suspense>
      </Modal>

      {/* Same as report templates: a change list and a wide diff open on top
            of Account Settings, and closing comes back here. */}
      <Modal
        title="Document History"
        isOpen={documentHistoryOpen}
        onClose={() => setDocumentHistoryOpen(false)}
        size="7xl"
        compact
      >
        <Suspense fallback={<p className="text-xs text-slate-400">Loading…</p>}>
          <DocumentChangeHistory userIdToken={accessToken} embedded />
        </Suspense>
      </Modal>
    </>
  );
};

export default ProfileSettingsModal;
